import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { nanoid } from 'nanoid';

import { datagridsx } from '../../../constants/datagridsx.js';
import { formatDateMMDDYYPacific } from '../../../utilities/dateHelpers';
import {
    getTotalDaysByCity,
    getStyleClassForDate,
} from '../../../utilities/acquisitionsHelpers';

const DateColumnWidth = 140;
const SecuredColumnWidth = 250;
const AssumptionColumnWidth = 200;

function flattenTargets(data) {
    const allTargets = {};

    for (const monthKey in data) {
        const month = data[monthKey];
        for (const targetKey in month.targets) {
            // assuming keys are unique across all months
            allTargets[targetKey] = month.targets[targetKey];
        }
    }

    return allTargets;
}

const getCutOffDatesForCities = (totalDaysByCity, tgtDate) => {
    const cutOffDates = {};
    const today1Month = new Date();
    today1Month.setDate(today1Month.getDate() + 28);

    for (let city in totalDaysByCity) {
        const cutOffDate = new Date(tgtDate);
        cutOffDate.setDate(cutOffDate.getDate() - totalDaysByCity[city]);
        cutOffDates[city] = cutOffDate;
    }
    return cutOffDates;
};

function stripStateZip(address) {
    const parts = address.split(',').map((p) => p.trim());
    if (parts.length >= 4) {
        return `${parts[0]}, ${parts[1]}`;
    }
    if (parts.length > 2) {
        return parts.slice(0, -2).join(', ');
    }
    return address; // unchanged if not in expected format
}

const AcquisitionsOverviewContents = ({ overview, assumptions }) => {
    const columnDefinitions = useMemo(() => {
        const cols = [
            {
                field: 'tgtDate',
                headerName: 'Target Date',
                type: 'date',
                width: DateColumnWidth,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                    <span className="acquisitions-overview-cell">
                        {formatDateMMDDYYPacific(params.value)}
                    </span>
                ),
            },
            {
                field: 'secured',
                headerName: 'Secured',
                width: SecuredColumnWidth,
                headerAlign: 'center',
                sortable: false,
                filterable: false,
                renderCell: (params) => {
                    const { actual, goal, date, properties } = params.value;
                    const today = new Date();
                    const tgtDate = new Date(date);
                    const color =
            actual >= goal
                ? 'acquisitions-good'
                : tgtDate < today
                    ? 'acquisitions-danger'
                    : 'acquisitions-warning';

                    if (actual === 0) {
                        return (
                            <div className={`${color} fst-italic acquisitions-overview-cell`}>
                                {`${actual} of ${goal}`}
                            </div>
                        );
                    }
                    return (
                        <div>
                            {properties.map((prop) => (
                                <>
                                    <Link
                                        key={prop.propertyId}
                                        target="_blank"
                                        to={{
                                            pathname: `/property/${prop.propertyId}`,
                                        }}
                                        className={`${color} text-capitalize acquisitions-overview-cell`}
                                    >
                                        {stripStateZip(prop.address.toLowerCase())}
                                    </Link>
                                </>
                            ))}
                        </div>
                    );
                },
            },
        ];
        assumptions.forEach((assumption) =>
            cols.push({
                field: assumption.label,
                headerName: assumption.label,
                renderHeader: (params) => {
                    if (!params.colDef) {
                        return null;
                    }
                    return (
                        <div
                            className="MuiDataGrid-columnHeaderTitle"
                            style={{ textAlign: 'center' }}
                        >
                            {params.colDef.headerName}
                        </div>
                    );
                },
                type: 'date',
                width: AssumptionColumnWidth,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                    <span className="">{formatDateMMDDYYPacific(params.value)}</span>
                ),
                cellClassName: (params) => {
                    if (!params.value) return '';
                    const today = new Date();
                    const today1Month = new Date();
                    today1Month.setDate(today1Month.getDate() + 28);
                    return getStyleClassForDate(params.value, today, today1Month);
                },
            })
        );
        return cols;
    }, [assumptions]);

    const totalDaysByCity = useMemo(
        () => getTotalDaysByCity(assumptions),
        [assumptions]
    );

    const tableRows = useMemo(() => {
        const targets = flattenTargets(overview);
        const rows = [];
        for (let tgt in targets) {
            const dates = getCutOffDatesForCities(totalDaysByCity, tgt);
            rows.push({
                id: nanoid(),
                tgtDate: new Date(tgt),
                ...dates,
                secured: {
                    actual: targets[tgt].securedActual,
                    goal: targets[tgt].securedGoal,
                    date: tgt,
                    properties: targets[tgt].securedProperties,
                },
            });
        }
        return rows;
    }, [totalDaysByCity, overview]);

    return (
        <div className="acquisitions-dashboard-container">
            <DataGridPro
                rows={tableRows}
                columns={columnDefinitions}
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'targetDate', sort: 'asc' }],
                    },
                }}
                getRowHeight={() => 'auto'}
                sx={{
                    ...datagridsx,
                    '& .MuiDataGrid-footerContainer': {
                        display: 'none',
                    },
                }}
            />
        </div>
    );
};

const AcquisitionsOverview = ({ overview, assumptions }) => {
    return assumptions.length === 0 ? (
        <div className="acquisitions-dashboard-container">
            <p>No Assumptions. Define assumptions in the assumptions tab.</p>
        </div>
    ) : (
        <AcquisitionsOverviewContents
            overview={overview}
            assumptions={assumptions}
        />
    );
};

export default AcquisitionsOverview;
